// Modules
import moment from "moment";
import { get } from "lodash";
import { useLocation, useNavigate } from "react-router-dom";
import { useState, useCallback, useEffect, useMemo } from "react";
import { AuthenticationDetails, CognitoUser, ICognitoUserSessionData } from "amazon-cognito-identity-js";

// Hooks
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";
import { FormDataValues, WatchWordValidationState, useFirstTimeLogin } from "containers/FirstTimeLogin/useFirstTimeLogin";
import { useLoginVerification } from "containers/LoginVerification/useLoginVerification";

// Constants
import { LoginPage, Routes, USER_INFO_COOKIE } from "constants/global";

// Redux
import {
	updateAuthFlowStatus,
	updateUserToken,
	getLoadingState,
	isLoggedInSelector,
	resetUserDetails,
	getUserAuthDetails,
	getLoggedInUserInfo,
	resetAuthErr,
	getInactiveTime
} from "redux/userList/userSlice";

// Utils
import { setCookie, getCookie, getAccessToken, invalidateCookie } from "utils/token";

// Auth provider
import { Auth } from "app/hooks/Auth";

// service calls
import { insertAuditLog, updateWatchwordResetLog } from "services/api/user";

export type LoginProps = {
	username: string;
	password: string;
};
export type LocationState = {
	state: {
		path: string;
		viaForgotWatchword?: boolean;
		username?: string;
		newWatchword?: string;
	};
};

type WatchWordChecksType = {
	isMinChars: boolean;
	isUpperCase: boolean;
	isLowerCase: boolean;
	isSpecialChar: boolean;
};

type FirstTimeLoginPropsType = {
	watchWord: string;
	watchWordChecks: WatchWordChecksType;
	confirmWatchword: string;
	isDetailsValid: boolean;
	isValidWatchWord: WatchWordValidationState;
	resetAll: () => void;
	validateWatchword: (value: string) => { checks: WatchWordChecksType; isPasswordInvalid: boolean };
	onWatchwordChange: (value: string) => void;
	onWatchwordBlur: () => void;
	setConfirmWatchword: (value: string) => void;
};

type LoginVerificationPropsType = {
	otpString: string;
	fieldErrs: { totp: boolean };
	validateTOTPString: () => void;
	setOTPString: (val: string) => void;
	setFieldErrs: (val: { totp: boolean }) => void;
};

function userHasGroup(groups: any, groupCode: string) {
	return Array.isArray(groups) ? groups?.some((obj: { group_code: string }) => obj.group_code === groupCode) : false;
}

export function useLogin() {
	const firstTimeLoginProps: FirstTimeLoginPropsType = useFirstTimeLogin();
	const loginVerificationProps: LoginVerificationPropsType = useLoginVerification();

	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const location = useLocation();

	const { state } = location as LocationState;

	const [inputType, setInputType] = useState<string>("password");
	const [cognitoUser, setCognitoUser] = useState<CognitoUser>();
	const [userName, setUserName] = useState("");
	const [watchWord, setWatchWord] = useState("");
	const [loginLoading, setLoginLoading] = useState<boolean>(false);
	const [errors, setErrors] = useState<{ credentialsErr?: boolean; attemptsErr?: boolean; disabledUserErr?: boolean }>({});

	const loadingData = useAppSelector(getLoadingState) as unknown as number;
	const isUserLoggedIn = useAppSelector(isLoggedInSelector);
	const loggedInUserData = useAppSelector(getLoggedInUserInfo);

	const { data: loggedInUserDetails, errors: logInError } = loggedInUserData || {};

	const [changingPW, setChangingPW] = useState<boolean>(false);
	const [setupOTP, setSetupOTP] = useState<boolean>(false);
	const [showMFASetup, setShowMFASetup] = useState<boolean>(false);

	const [qrCodeImg, setQRCodeImg] = useState<string>("");
	const [qrCodeStr, setQRCodeStr] = useState<string>("");

	const [isInvalidTOTPErr, setInvalidTOTP] = useState<boolean>(false);
	const [sessionTimeoutErr, setSessionTimeoutErr] = useState<boolean>(false);

	const updateWatchwordLastUpdateDate = async () => {
		await updateWatchwordResetLog();
	};

	const onUserLoginSuccess = async (data: any, skip: boolean) => {
		const cookie_ttl = parseInt(data.idToken.payload.exp) - parseInt(data.idToken.payload.iat);
		setCookie(USER_INFO_COOKIE, JSON.stringify(data.idToken), cookie_ttl);

		if (state?.viaForgotWatchword && state?.username && state?.newWatchword) {
			await updateWatchwordLastUpdateDate();
		}
		dispatch(updateUserToken(data));
		// fetch auth matrix immediately after login
		if (!skip) {
			dispatch(getUserAuthDetails());
			insertAuditLog({
				action_type_code: LoginPage.AUDIT_ACTION_TYPE_CODE,
				program_name: LoginPage.AUDIT_ACTION,
				effected_user: userName,
				log_message: "",
				error: ""
			});
			dispatch(getInactiveTime());
		}
	};

	const callbacks: any = {
		onFailure: (err: any) => {
			if (err?.message === LoginPage.CREDENTIAL_ERR_RESPONSE) {
				setErrors({ credentialsErr: true });
			}
			if (err?.message === LoginPage.ATTEMPTS_ERR_RESPONSE) {
				setErrors({ attemptsErr: true });
			}
			if (err?.message === LoginPage.USER_DISABLED_ERR_RESPONSE) {
				setErrors({ disabledUserErr: true });
			}
			setLoginLoading(false);
		},
		/**
		 * @description Save user details after successful login.
		 * If user required to enter MFA TOTP then it will call after totpRequired callback
		 */
		onSuccess: async (result: ICognitoUserSessionData) => {
			if (result) {
				const userGroup = get(result, "idToken.payload.cognito:groups", "");
				const _cognitoUser = new Auth().getCognitoUser();
				if (_cognitoUser && userGroup !== "" && userGroup.includes("_mfa")) {
					_cognitoUser.associateSoftwareToken(callbacks);
					dispatch(updateAuthFlowStatus(true));
					await onUserLoginSuccess(result, true);
				} else {
					await onUserLoginSuccess(result, false);
				}
			}
			setLoginLoading(false);
		},
		/**
		 * @description this will call when user is logging first time.
		 * It will ask user to set new watchword.
		 */
		newPasswordRequired: function () {
			setLoginLoading(false);
			// User was signed up by an admin and must provide new
			// watchword and required attributes, if any, to complete
			// authentication.
			setChangingPW(true);
		},
		/**
		 * @description this will call after new watchword success if user is part of _mfa group.
		 * This will navigate to verification page and will show the QR Code to setup the MFA app (Google Authenticator, Microsoft Authenticator).
		 * Your need to enter the TOTP after MFA has been setup.
		 * Once your click on verify after entering TOTP will navigate to landing page.
		 */
		associateSecretCode: function (secretCode: string) {
			setLoginLoading(false);
			const str = `otpauth://totp/${userName}?secret=${secretCode}&issuer=CRAdminPortal&algorithm=SHA1&digits=6&period=30`;

			setQRCodeImg(str);
			setQRCodeStr(secretCode);
			setSetupOTP(true);
			setShowMFASetup(true);
		},
		/**
		 * @description this will call after login if user is part of _mfa group.
		 * Once your click on verify after entering TOTP will navigate to landing page.
		 */
		totpRequired: function () {
			setLoginLoading(false);
			setSetupOTP(true);
		}
	};

	useEffect(() => {
		dispatch(resetUserDetails());

		if (getCookie("USER_INFO_COOKIE")) {
			const tokenDetails = JSON.parse(getCookie("USER_INFO_COOKIE"));
			dispatch(updateUserToken(tokenDetails));
		}
	}, []);

	useEffect(() => {
		if (cognitoUser && userName && watchWord) {
			const authenticationDetails = new AuthenticationDetails({
				Username: userName,
				Password: watchWord
			});

			cognitoUser.authenticateUser(authenticationDetails, callbacks);
			setErrors({});
		}
	}, [cognitoUser]);

	const handleSubmit = useCallback(
		(values: LoginProps) => {
			const { username, password: rawPassword } = values;
			setUserName(username.trim());
			setWatchWord(rawPassword.trim());
			if (username && rawPassword) {
				setLoginLoading(true);

				const auth = new Auth();
				auth.init(username.trim());

				const user = auth.getCognitoUser();
				setCognitoUser(user);
			}
		},
		[userName, watchWord]
	);

	const toggleType = useCallback(() => {
		setInputType(inputType === "password" ? "text" : "password");
	}, [inputType, setInputType]);

	const onChangePW = (values: FormDataValues) => {
		const { isPasswordInvalid } = firstTimeLoginProps.validateWatchword(values?.password);
		if (firstTimeLoginProps?.isDetailsValid && firstTimeLoginProps?.watchWord === firstTimeLoginProps?.confirmWatchword && !isPasswordInvalid) {
			const user = new Auth().getCognitoUser();
			if (user) {
				// update new watchword for first login
				user.completeNewPasswordChallenge(firstTimeLoginProps?.watchWord, [], {
					onSuccess: (result) => {
						callbacks.onSuccess(result);
						setChangingPW(false);
						setSessionTimeoutErr(false);
						updateWatchwordLastUpdateDate();
					},
					onFailure: (err) => {
						if (err?.message === LoginPage.INVALID_SESSION_TIMEOUT) {
							setSessionTimeoutErr(true);
							firstTimeLoginProps.resetAll();
							setSetupOTP(false);
							setChangingPW(false);
						}
					}
				});
			}
		}
	};

	const onLinkOTP = () => {
		const _cognitoUser = new Auth().getCognitoUser();
		loginVerificationProps.setFieldErrs({ totp: false });
		loginVerificationProps.validateTOTPString();

		if (_cognitoUser && !loginVerificationProps.fieldErrs.totp) {
			_cognitoUser.verifySoftwareToken(loginVerificationProps.otpString, LoginPage.MY_TOTP_DEVICE, {
				onSuccess: () => {
					const totpMfaSettings = {
						PreferredMfa: true,
						Enabled: true
					};
					_cognitoUser.setUserMfaPreference(null, totpMfaSettings, function (err) {
						if (!err) {
							setWatchWord("");
							setUserName("");
							dispatch(updateAuthFlowStatus(false));
							navigate("/");
						}
					});
					setSetupOTP(false);
					setShowMFASetup(false);
				},
				onFailure: (err) => {
					if (err) {
						setSetupOTP(false);
						setShowMFASetup(false);
					}
				}
			});
		}
	};

	const onVerifyOTP = () => {
		loginVerificationProps.setFieldErrs({ totp: false });
		loginVerificationProps.validateTOTPString();

		const _cognitoUser = new Auth().getCognitoUser();
		if (_cognitoUser && !loginVerificationProps.fieldErrs.totp) {
			setLoginLoading(true);
			_cognitoUser.sendMFACode(
				loginVerificationProps.otpString,
				{
					onSuccess: (result) => {
						setSetupOTP(false);
						onUserLoginSuccess(result, false);
						setInvalidTOTP(false);
					},
					onFailure: (err) => {
						if (err?.message === LoginPage.INVALID_CODE_ERR) {
							setInvalidTOTP(true);
						}
						setLoginLoading(false);
					}
				},
				LoginPage.SOFTWARE_TOKEN_MFA
			);
		}
	};

	useMemo(() => {
		/**
		 * After unsuccessful login redirect the user to login page.
		 */
		if (logInError?.length) {
			const userPool = new Auth().getUserPool();
			const currentUser = userPool?.getCurrentUser();
			if (currentUser) {
				currentUser?.signOut();
			}

			invalidateCookie();
			navigate(Routes.LOGIN);
		}

		/**
		 * After successful login if user watchword is older than 180 days (6 months)
		 * then redirect the user to forgot watchword page forcefully by logging out the user.
		 */
		if (isUserLoggedIn && loggedInUserDetails && loggedInUserDetails?.length && getAccessToken()) {
			const userDetails = loggedInUserDetails[0];
			const lastPwdUpdateDate = moment(userDetails?.pwd_updated_on);
			const currentDate = moment(new Date());
			const diff = currentDate.diff(lastPwdUpdateDate, "days");

			const expiryDays = userHasGroup(userDetails?.groups, "TENANT") ? LoginPage.FORCE_UPDATE_WATCHWORD_EXPIRY_DAYS_FOR_TENANTS : LoginPage.FORCE_UPDATE_WATCHWORD_EXPIRY_DAYS;
			if (diff > expiryDays) {
				const userPool = new Auth().getUserPool();
				const currentUser = userPool?.getCurrentUser();
				if (currentUser) {
					currentUser?.signOut();
				}

				invalidateCookie();
				navigate(Routes.FORGOT_WATCHWORD, { state: { forceChangeWatchword: true } });
			}
		}

		/**
		 * After successful login redirect user to home page or redirect to previous bookmarked page.
		 */
		if (isUserLoggedIn && loggedInUserDetails && loggedInUserDetails?.length && getAccessToken()) {
			const parentRoute = state?.path?.split("/")[1];

			const navigateTo = state?.path ? `/${parentRoute}` : "/";
			dispatch(resetAuthErr());
			navigate(navigateTo);
		}
	}, [state, logInError, loggedInUserDetails, isUserLoggedIn, invalidateCookie, dispatch, resetUserDetails, getAccessToken, navigate]);

	/**
	 * After forgot watchword has been reset login to app using new watchword
	 */
	useMemo(() => {
		const { viaForgotWatchword, username, newWatchword } = state || {};
		if (viaForgotWatchword && username && newWatchword) {
			handleSubmit({
				username: username,
				password: newWatchword
			});
		}
	}, []);

	const backToLogin = useCallback(() => {
		setSetupOTP(false);
		setChangingPW(false);
		setShowMFASetup(false);
		setUserName("");
		setWatchWord("");
		firstTimeLoginProps.resetAll();
	}, [firstTimeLoginProps]);

	return {
		errors,
		inputType,
		loginError: logInError?.length,
		loading: loadingData === 1 || loginLoading,
		changingPW,
		setupOTP,
		sessionTimeoutErr,
		toggleType,
		handleSubmit,
		firstTimeLoginProps: { ...firstTimeLoginProps, onChangePW, backToLogin },
		loginVerificationProps: { ...loginVerificationProps, isInvalidTOTPErr, showSetup: showMFASetup, onLinkOTP, onVerifyOTP, QRCodeImage: qrCodeImg, qrCodeStr, backToLogin }
	};
}
